import * as React from "react";

import * as DV from "@impero-com/common/utils/dataView";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { Divider } from "@impero-com/ui";

import { EntityView } from "api/generated/model";
import { StringInput } from "components/form/legacyFields/stringInput";
import { TextAreaInput } from "components/form/legacyFields/textAreaInput";
import * as R from "org/risks/types";
import { UpdateRootNodeLinkedResourcesCallback } from "org/risks/widgets/attachedFilesField";
import {
    NodeScope,
    NodeScopeValues,
    initialScope as getInitialScope,
    initialEntityId,
} from "org/risks/widgets/nodeScope";
import { FormComponentProps, subForm } from "utils/form";
import { Guid } from "utils/guid";

import { MetadataFieldSectionsLayout } from "../sections";
import * as T from "../types";
import * as U from "../utils";

class CreateNodeDetailsTab<ROOT> extends React.PureComponent<
    CreateNodeDetailsTabProps<ROOT>
> {
    private nameInput = React.createRef<StringInput<"name", ROOT>>();

    componentDidMount = () => {
        // Consume parent state before checking actual reference
        // which is supposed to always be set.
        if (this.props.focusOnMount()) {
            if (this.nameInput.current) {
                this.nameInput.current.giveFocus();
            }
        }
    };

    render = () => {
        const {
            form,
            buildNode,
            riskDirectoryView,
            metadataExtractors,
            entities,
            permission,
            initialScope,
            parentNode,
        } = this.props;
        const detailForm = subForm("details", form);

        const newNode = buildNode();

        const { sections, matchCount: nbFields } =
            U.filterAndResolveDirectorySections(
                riskDirectoryView,
                metadataExtractors,
                newNode,
                {
                    riskDirectoryLevel: true,
                    entityLevel: false,
                }
            );

        return (
            <>
                <StringInput
                    ref={this.nameInput}
                    id="name"
                    initialValue={undefined}
                    type="CREATE"
                    label={t("RiskDirectory_NodeUpdate_Field_Name")}
                    form={detailForm}
                    validators={[
                        {
                            kind: "ruleset",
                            required: true,
                            whitespace: true,
                        },
                        {
                            kind: "custom",
                            validator: (nodeName: string) => {
                                let nodeScope;
                                const nodeScopeType = getInitialScope(
                                    permission,
                                    detailForm.getFieldsValue().scope ??
                                        initialScope
                                );
                                switch (nodeScopeType) {
                                    case "Global":
                                        nodeScope = {
                                            scope: nodeScopeType,
                                        };
                                        break;
                                    case "Local":
                                        nodeScope = {
                                            scope: nodeScopeType,
                                            entityId: initialEntityId(
                                                permission,
                                                detailForm.getFieldsValue()
                                                    .scope ?? initialScope
                                            ),
                                        };
                                        break;
                                    default:
                                        assertNever(nodeScopeType);
                                }
                                return U.validateNodeName(
                                    nodeName,
                                    newNode.id,
                                    newNode.type,
                                    nodeScope,
                                    parentNode
                                );
                            },
                        },
                    ]}
                />
                <TextAreaInput
                    id="description"
                    initialValue={undefined}
                    type="CREATE"
                    label={t("RiskDirectory_NodeUpdate_Field_Description")}
                    form={detailForm}
                />
                <Divider />
                <NodeScope
                    id="scope"
                    type="CREATE"
                    nodeType={newNode.type}
                    entities={entities}
                    permission={permission}
                    form={detailForm}
                    initialValue={initialScope}
                />
                {nbFields > 0 ? (
                    <>
                        <Divider />
                        <MetadataFieldSectionsLayout
                            id="metadataValues"
                            type="CREATE"
                            sections={sections}
                            node={newNode}
                            extractors={metadataExtractors}
                            form={detailForm}
                            updateRootNodeLinkedResources={
                                this.props.updateRootNodeLinkedResources
                            }
                        />
                    </>
                ) : null}
            </>
        );
    };
}

interface CreateNodeDetailsTabProps<ROOT> extends FormComponentProps<
    "details",
    CreateNodeDetailsTabValues,
    ROOT
> {
    nodeType: "Process" | "Risk";
    parentNode: R.ParentNode;
    buildNode: () => R.NewRiskDirectoryNode;
    metadataExtractors: Array<
        DV.DataExtractor<T.NodeWithMD, T.MetadataInstance>
    >;
    riskDirectoryView: R.RiskDirectoryView;
    focusOnMount: () => boolean;
    entities: Record<number, EntityView>;
    permission: T.NodeCreationAllowedPermission;
    initialScope?: T.NodeScope;
    updateRootNodeLinkedResources: UpdateRootNodeLinkedResourcesCallback;
}

interface CreateNodeDetailsTabValues {
    name: string;
    description: string;
    metadataValues: Record<Guid, T.MetadataInstance>;
    scope: NodeScopeValues;
}

export {
    CreateNodeDetailsTab,
    type CreateNodeDetailsTabProps,
    type CreateNodeDetailsTabValues,
};
